import { useState, useEffect } from 'react';
import { Calendar, AlertTriangle, AlertCircle, Filter, FileText, User, Truck, MapPin, ArrowLeft, Edit, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubmittedLogs = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [issueFilter, setIssueFilter] = useState(false);
  const [accidentFilter, setAccidentFilter] = useState(false);
  const [driverFilter, setDriverFilter] = useState('all');
  const [drivers, setDrivers] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showFullLogDialog, setShowFullLogDialog] = useState(false);
  const [fullLogData, setFullLogData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    fetchLogs();
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Filter for drivers
      const driverList = data.filter(emp => {
        const dept = (emp.department || '').toLowerCase();
        const jobTitle = (emp.job_title || '').toLowerCase();
        return dept === 'drivers' || jobTitle === 'driver';
      });
      setDrivers(driverList);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/driver-logs/submitted`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch logs');

      const data = await response.json();
      setLogs(data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load submitted logs');
    } finally {
      setLoading(false);
    }
  };

  const handleViewFullLog = async (log) => {
    try {
      const token = localStorage.getItem('token');
      
      // Reset edit mode before opening new log
      setIsEditMode(false);
      setEditFormData({});
      
      // Fetch the complete log from backend
      if (log.log_id) {
        const response = await fetch(`${BACKEND_URL}/api/driver-logs/${log.log_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch full log');
        }

        const fullLog = await response.json();
        setFullLogData(fullLog);
      } else {
        // Use the log data we already have
        setFullLogData(log);
      }
      
      setShowFullLogDialog(true);
    } catch (error) {
      console.error('Error fetching full log:', error);
      toast.error('Failed to load full log details');
    }
  };

  const handleCloseDialog = () => {
    setShowFullLogDialog(false);
    setIsEditMode(false);
    setEditFormData({});
    setFullLogData(null);
  };

  const getFilteredLogs = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return logs.filter(log => {
      // Date filtering
      const logDate = new Date(log.schedule_date);
      logDate.setHours(0, 0, 0, 0);

      let passesDateFilter = true;
      if (dateFilter === 'today') {
        passesDateFilter = logDate.getTime() === today.getTime();
      } else if (dateFilter === 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        passesDateFilter = logDate.getTime() === yesterday.getTime();
      } else if (dateFilter === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        passesDateFilter = logDate.getTime() === tomorrow.getTime();
      } else if (dateFilter === 'wtd') {
        // Week to date - from Monday to today
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1); // Get Monday
        monday.setHours(0, 0, 0, 0);
        passesDateFilter = logDate >= monday && logDate <= today;
      } else if (dateFilter === 'mtd') {
        // Month to date - from 1st of month to today
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        firstOfMonth.setHours(0, 0, 0, 0);
        passesDateFilter = logDate >= firstOfMonth && logDate <= today;
      } else if (dateFilter === 'ytd') {
        // Year to date - from 1st of Jan to today
        const firstOfYear = new Date(today.getFullYear(), 0, 1);
        firstOfYear.setHours(0, 0, 0, 0);
        passesDateFilter = logDate >= firstOfYear && logDate <= today;
      } else if (dateFilter === 'custom') {
        // Custom date range - from start to end
        if (customStartDate && customEndDate) {
          const startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
          passesDateFilter = logDate >= startDate && logDate <= endDate;
        } else if (customStartDate) {
          const startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
          passesDateFilter = logDate >= startDate;
        } else if (customEndDate) {
          const endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
          passesDateFilter = logDate <= endDate;
        }
      }

      // Issue filtering
      if (issueFilter && !log.issue_reported) {
        return false;
      }

      // Accident filtering
      if (accidentFilter && !log.accident_reported) {
        return false;
      }

      // Driver filtering
      if (driverFilter !== 'all' && log.driver_id !== driverFilter) {
        return false;
      }

      return passesDateFilter;
    });
  };

  const filteredLogs = getFilteredLogs();

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Submitted Logs</h2>
              <p className="text-sm text-gray-500">Review driver logs and reports</p>
            </div>
          </div>
          <Button
            onClick={fetchLogs}
            className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto"
          >
            <FileText className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        </div>

        <div className={`grid gap-4 ${dateFilter === 'custom' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-6' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
          {/* Driver Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Driver</label>
            <select
              value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Drivers</option>
              {drivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.first_name && driver.last_name 
                    ? `${driver.first_name} ${driver.last_name}` 
                    : driver.name || driver.email}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="wtd">Week to Date</option>
              <option value="mtd">Month to Date</option>
              <option value="ytd">Year to Date</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Range Pickers */}
          {dateFilter === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
            </>
          )}

          {/* Issue Reported Filter */}
          <div className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg">
            <input
              type="checkbox"
              id="issue-filter"
              checked={issueFilter}
              onChange={(e) => setIssueFilter(e.target.checked)}
              className="w-5 h-5 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <label htmlFor="issue-filter" className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <AlertCircle className="w-4 h-4 text-orange-500" />
              Issue Reported
            </label>
          </div>

          {/* Accident Reported Filter */}
          <div className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg">
            <input
              type="checkbox"
              id="accident-filter"
              checked={accidentFilter}
              onChange={(e) => setAccidentFilter(e.target.checked)}
              className="w-5 h-5 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <label htmlFor="accident-filter" className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              Accident Reported
            </label>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing <span className="font-semibold">{filteredLogs.length}</span> of <span className="font-semibold">{logs.length}</span> logs
        </p>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading logs...</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No logs found</h3>
          <p className="text-gray-600">No submitted logs match your current filters.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLogs.map((log) => (
            <div
              key={log.id}
              className={`bg-white rounded-xl shadow-md p-4 sm:p-6 cursor-pointer transition-all hover:shadow-lg ${
                selectedLog?.id === log.id ? 'ring-2 ring-teal-500' : ''
              }`}
              onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  {/* Driver & Date */}
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-teal-600" />
                      <span className="font-bold text-gray-900">{log.driver_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">{new Date(log.schedule_date).toLocaleDateString('en-GB')}</span>
                    </div>
                  </div>

                  {/* Vehicle & Route */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Truck className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{log.vehicle_registration}</span>
                    </div>
                    {log.route && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700">{log.route}</span>
                      </div>
                    )}
                  </div>

                  {/* Alerts */}
                  <div className="flex flex-wrap gap-2">
                    {log.issue_reported && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                        <AlertCircle className="w-3 h-3" />
                        Issue Reported
                      </span>
                    )}
                    {log.accident_reported && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                        <AlertTriangle className="w-3 h-3" />
                        Accident Reported
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      log.status === 'submitted' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {log.status === 'submitted' ? 'Submitted' : log.status}
                    </span>
                  </div>
                </div>

                {/* Time Info */}
                <div className="lg:text-right">
                  <div className="text-sm text-gray-600 mb-1">
                    {log.start_time} - {log.end_time}
                  </div>
                  <div className="text-xs text-gray-500">
                    Submitted: {log.submitted_at ? new Date(log.submitted_at).toLocaleString('en-GB') : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {selectedLog?.id === log.id && (
                <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
                  {/* Mileage */}
                  {log.mileage_start && log.mileage_end && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Start Mileage</label>
                        <p className="text-gray-900">{log.mileage_start} miles</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">End Mileage</label>
                        <p className="text-gray-900">{log.mileage_end} miles</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Total Miles</label>
                        <p className="text-gray-900 font-semibold">{log.mileage_end - log.mileage_start} miles</p>
                      </div>
                    </div>
                  )}

                  {/* Issue Details */}
                  {log.issue_reported && log.issue_description && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        Issue Report
                      </h4>
                      <p className="text-sm text-orange-800">{log.issue_description}</p>
                    </div>
                  )}

                  {/* Accident Details */}
                  {log.accident_reported && log.accident_description && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Accident Report
                      </h4>
                      <p className="text-sm text-red-800">{log.accident_description}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {log.notes && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2">Notes</label>
                      <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{log.notes}</p>
                    </div>
                  )}

                  {/* View Full Log Button */}
                  <div className="pt-4 border-t border-gray-200">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewFullLog({...log, log_id: log.log_id || log.id});
                      }}
                      variant="outline"
                      className="w-full"
                    >
                      View Full Log
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Full Log Dialog - Same as Reports page */}
      {showFullLogDialog && fullLogData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Dialog Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">{isEditMode ? 'Edit Driver Log' : 'Complete Driver Log'}</h2>
                <div className="flex items-center gap-2">
                  {!isEditMode ? (
                    <button
                      onClick={() => {
                        setIsEditMode(true);
                        setEditFormData({
                          mileage_start: fullLogData.mileage_start || fullLogData.start_mileage || 0,
                          mileage_end: fullLogData.mileage_end || fullLogData.end_mileage || 0,
                          truck_washed_inside: fullLogData.truck_washed_inside || false,
                          truck_washed_outside: fullLogData.truck_washed_outside || false,
                          notes: fullLogData.notes || '',
                          clock_on_time: fullLogData.clock_on_time || '',
                          clock_off_time: fullLogData.clock_off_time || ''
                        });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const logId = fullLogData.id || fullLogData.log_id;
                            const response = await fetch(`${BACKEND_URL}/api/driver-logs/${logId}/edit`, {
                              method: 'PUT',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify(editFormData)
                            });
                            
                            if (!response.ok) throw new Error('Failed to save');
                            
                            toast.success('Log updated successfully');
                            handleCloseDialog();
                            fetchLogs();
                          } catch (error) {
                            toast.error('Failed to update log');
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Save
                      </button>
                      <button
                        onClick={() => setIsEditMode(false)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleCloseDialog}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Dialog Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              <div className="space-y-6">
                {/* Driver & Vehicle Info */}
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                  <h3 className="font-bold text-emerald-900 mb-3">Driver & Vehicle Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Driver Name</p>
                      <p className="font-semibold">{fullLogData.driver_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Vehicle Registration</p>
                      <p className="font-semibold">{fullLogData.vehicle_registration || fullLogData.vehicle_id || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Date</p>
                      <p className="font-semibold">{fullLogData.schedule_date || fullLogData.date || fullLogData.submitted_at?.split('T')[0] || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <p className="font-semibold capitalize">{fullLogData.status || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <h3 className="font-bold text-purple-900 mb-3">Timestamps</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Clock On Time</p>
                      {isEditMode ? (
                        <Input
                          type="time"
                          value={editFormData.clock_on_time}
                          onChange={(e) => setEditFormData({...editFormData, clock_on_time: e.target.value})}
                          className="font-semibold"
                        />
                      ) : (
                        <p className="font-semibold">
                          {fullLogData.clock_in_timestamp 
                            ? new Date(fullLogData.clock_in_timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                            : fullLogData.clock_on_time || fullLogData.ClockInTime || 'N/A'}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Clock Off Time</p>
                      {isEditMode ? (
                        <Input
                          type="time"
                          value={editFormData.clock_off_time}
                          onChange={(e) => setEditFormData({...editFormData, clock_off_time: e.target.value})}
                          className="font-semibold"
                        />
                      ) : (
                        <p className="font-semibold">
                          {fullLogData.clock_out_timestamp 
                            ? new Date(fullLogData.clock_out_timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                            : fullLogData.clock_off_time || fullLogData.ClockOutTime || 'N/A'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Mileage Info */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h3 className="font-bold text-blue-900 mb-3">Mileage Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Start Mileage</p>
                      {isEditMode ? (
                        <Input
                          type="number"
                          value={editFormData.mileage_start}
                          onChange={(e) => setEditFormData({...editFormData, mileage_start: parseFloat(e.target.value) || 0})}
                          className="font-semibold"
                        />
                      ) : (
                        <p className="font-semibold">{fullLogData.mileage_start || fullLogData.start_mileage || 0} mi</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">End Mileage</p>
                      {isEditMode ? (
                        <Input
                          type="number"
                          value={editFormData.mileage_end}
                          onChange={(e) => setEditFormData({...editFormData, mileage_end: parseFloat(e.target.value) || 0})}
                          className="font-semibold"
                        />
                      ) : (
                        <p className="font-semibold">{fullLogData.mileage_end || fullLogData.end_mileage || 0} mi</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Distance</p>
                      <p className="font-semibold text-blue-700">
                        {isEditMode 
                          ? ((editFormData.mileage_end || 0) - (editFormData.mileage_start || 0))
                          : ((fullLogData.mileage_end || fullLogData.end_mileage || 0) - (fullLogData.mileage_start || fullLogData.start_mileage || 0))
                        } mi
                      </p>
                    </div>
                  </div>
                </div>

                {/* Truck Wash Info */}
                {(fullLogData.truck_washed_inside !== undefined || fullLogData.truck_washed_outside !== undefined || isEditMode) && (
                  <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-200">
                    <h3 className="font-bold text-cyan-900 mb-3">Vehicle Wash Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        {isEditMode ? (
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editFormData.truck_washed_inside}
                              onChange={(e) => setEditFormData({...editFormData, truck_washed_inside: e.target.checked})}
                              className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                            />
                            <span className="font-medium">Washed Inside</span>
                          </label>
                        ) : (
                          <>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${fullLogData.truck_washed_inside ? 'bg-green-500' : 'bg-gray-300'}`}>
                              {fullLogData.truck_washed_inside && (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className="font-medium">Washed Inside</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {isEditMode ? (
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editFormData.truck_washed_outside}
                              onChange={(e) => setEditFormData({...editFormData, truck_washed_outside: e.target.checked})}
                              className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                            />
                            <span className="font-medium">Washed Outside</span>
                          </label>
                        ) : (
                          <>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${fullLogData.truck_washed_outside ? 'bg-green-500' : 'bg-gray-300'}`}>
                              {fullLogData.truck_washed_outside && (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className="font-medium">Washed Outside</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {(fullLogData.notes || isEditMode) && (
                  <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                    <h3 className="font-bold text-yellow-900 mb-3">Notes / Issues</h3>
                    {isEditMode ? (
                      <textarea
                        value={editFormData.notes}
                        onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                        className="w-full p-3 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 min-h-[100px]"
                        placeholder="Enter any notes or issues..."
                      />
                    ) : (
                      <p className="text-gray-700 whitespace-pre-wrap">{fullLogData.notes}</p>
                    )}
                  </div>
                )}

                {/* Accident Information */}
                {(fullLogData.had_accident || fullLogData.accident_reported) && (
                  <div className="bg-red-50 rounded-xl p-4 border-2 border-red-300">
                    <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Accident Reported
                    </h3>
                    
                    {fullLogData.was_third_party_involved && (
                      <div className="space-y-3">
                        <p className="font-semibold text-red-800">3rd Party Involved</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {fullLogData.accident_third_party_name && (
                            <div>
                              <p className="text-sm text-gray-600">3rd Party Name</p>
                              <p className="font-semibold">{fullLogData.accident_third_party_name}</p>
                            </div>
                          )}
                          {fullLogData.accident_contact_number && (
                            <div>
                              <p className="text-sm text-gray-600">Contact Number</p>
                              <p className="font-semibold">{fullLogData.accident_contact_number}</p>
                            </div>
                          )}
                          {fullLogData.accident_registration && (
                            <div>
                              <p className="text-sm text-gray-600">Vehicle Registration</p>
                              <p className="font-semibold">{fullLogData.accident_registration}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Accident Images */}
                        {fullLogData.accident_images && fullLogData.accident_images.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm text-gray-600 mb-2">Accident Images ({fullLogData.accident_images.length})</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {fullLogData.accident_images.map((imgPath, idx) => (
                                <a
                                  key={idx}
                                  href={`${BACKEND_URL}/${imgPath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img
                                    src={`${BACKEND_URL}/${imgPath}`}
                                    alt={`Accident ${idx + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border-2 border-red-200 hover:border-red-400 transition-colors cursor-pointer"
                                  />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {!fullLogData.was_third_party_involved && fullLogData.had_accident && (
                      <p className="text-red-700">No 3rd party involved</p>
                    )}
                  </div>
                )}

                {/* Timestamps */}
                {fullLogData.submitted_at && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-3">Timestamps</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Submitted At</p>
                        <p className="font-medium">{new Date(fullLogData.submitted_at).toLocaleString()}</p>
                      </div>
                      {fullLogData.created_at && (
                        <div>
                          <p className="text-gray-600">Created At</p>
                          <p className="font-medium">{new Date(fullLogData.created_at).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
              <Button
                onClick={() => setShowFullLogDialog(false)}
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmittedLogs;
